import { Component, OnDestroy, OnInit } from '@angular/core';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';

import { untilDestroyed } from '@app/core';
import { CurrencyOptions, OrganizationGroup, User } from '@app/models';
import { AppService, ErrorHandlerService, OrganizationService, UserService } from '@app/services';
import { AlertModalService } from '@app/shared';
import { CreateOrganizationGroupModalService } from './create-organization-group-modal/create-organization-group-modal.service';
import { EditOrganizationGroupModalService } from './edit-organization-group-modal/edit-organization-group-modal.service';
import { EditOrganizationGroupPermissionsModalService } from './edit-organization-group-permissions-modal/edit-organization-group-permissions-modal.service';
import { EditOrganizationGroupAppearanceModalService } from './edit-organization-group-appearance-modal/edit-organization-group-appearance-modal.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-configurations-organization-groups',
  templateUrl: './configurations-organization-groups.component.html',
  styleUrls: ['../configurations.scss', './configurations-organization-groups.component.scss'],
  standalone: false
})
export class ConfigurationsOrganizationGroupsComponent implements OnInit, OnDestroy {
  currentUser: User;
  isLoading = false;
  isLastPage = false;
  pageNumber: number;
  groups: OrganizationGroup[] = [];
  total: number;
  currency: CurrencyOptions = 'BRL';

  constructor(
    public userService: UserService,
    public organizationService: OrganizationService,
    public appService: AppService,
    private errorHandlerService: ErrorHandlerService,
    private alertModalService: AlertModalService,
    private createOrganizationGroupModalService: CreateOrganizationGroupModalService,
    private editOrganizationGroupModalService: EditOrganizationGroupModalService,
    private editOrganizationGroupPermissionsModalService: EditOrganizationGroupPermissionsModalService,
    private editOrganizationGroupAppearanceModalService: EditOrganizationGroupAppearanceModalService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        tap(user => (this.currency = this.appService.currencyForCountry(user.organization.country))),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => (this.currentUser = user)
      });

    this.loadGroups();
  }

  ngOnDestroy() {}

  loadGroups(options: { page?: number } = {}) {
    this.isLoading = true;
    this.pageNumber = options.page || 1;

    this.userService
      .getCurrentUser({ fetchPolicy: 'cache-first' })
      .pipe(
        switchMap(user => {
          return this.organizationService.organizationGroups({ page: this.pageNumber, limit: 60, organizationId: user.organization.id });
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: page => {
          this.total = page.total - 1; // Tirando o "Sem grupo"
          this.groups = (this.pageNumber === 1 ? [] : this.groups || []).concat(page.data);
          this.isLastPage = page.current_page >= page.last_page;
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  openCreateGroupModal() {
    this.createOrganizationGroupModalService.open().subscribe({
      next: () => this.loadGroups()
    });
  }

  openEditGroupModal(group: OrganizationGroup) {
    this.editOrganizationGroupModalService.open({ group }).subscribe({
      next: data => (this.groups[this.groups.findIndex(item => item.id === data.id)] = data)
    });
  }

  openEditGroupPermissionsModal(group: OrganizationGroup) {
    this.editOrganizationGroupPermissionsModalService.open({ group }).subscribe({
      next: data => (this.groups[this.groups.findIndex(item => item.id === data.id)] = data)
    });
  }

  openEditGroupAppearanceModal(group: OrganizationGroup) {
    this.editOrganizationGroupAppearanceModalService.open({ group }).subscribe({
      next: data => (this.groups[this.groups.findIndex(item => item.id === data.id)] = data)
    });
  }

  deleteGroup(group: OrganizationGroup) {
    this.alertModalService
      .warning({
        text: this.translateService.instant('alerts.deleteGroup'),
        confirmButtonText: this.translateService.instant('button.exclude')
      })
      .pipe(
        switchMap(() => {
          this.isLoading = true;
          return this.organizationService.deleteGroup({ id: group.id }).pipe(finalize(() => (this.isLoading = false)));
        })
      )
      .subscribe({
        next: () => this.loadGroups(),
        error: error => {
          this.errorHandlerService.handle(error);
        }
      });
  }

  additionalGroupsCount() {
    return this.total >= 0 ? Math.max(this.total - this.organizationService.maxFreeGroups, 0) : 0;
  }

  returnGroupName(name: string) {
    if (name === 'Sem Grupo' || name === 'Sem grupo') {
      return this.translateService.instant('settings.withoutGroup');
    }
    if (name === 'Administrador') {
      return this.translateService.instant('settings.admin');
    }
    return name;
  }
}
